import React, {useState, useRef, useCallback, useMemo, useContext} from 'react';
import classnames from 'classnames';
import {
  FindAndApply,
  Button,
  Icon,
  IconButton,
  Popover,
  ButtonGroup,
  addGenericErrorToast
} from '@albert-io/atomic';
import {ButtonVariant, ButtonColor} from '@albert-io/atomic/atoms/Button/Button.react';
import {List} from 'immutable';
import {pluralize} from 'lib/stringUtils';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import {useTracking} from 'react-tracking';

import {getSubjectsQuery} from '../TopicsModal.queries';

import SubjectsFilterResults from './SubjectsFilterResults.react';

import './subjects-filter.scss';

type Subject = any;

interface Props {
  selectHandler: (subject: Subject) => void;
  selectedSubjects: List<Subject>;
  classroomIds: string[];
  onClear: () => void;
}

const SubjectsFilter = ({selectedSubjects, selectHandler, classroomIds, onClear}: Props) => {
  const [showPopover, setShowPopover] = useState(false);
  const buttonRef = useRef(null);
  const {
    questionDifficulties,
    questionTypes,
    standardCollection,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    getSelectedStandardsAndSets
  } = useContext(ContentDiscoveryContext);

  const {selectedStandardIds, selectedStandardSetIds} =
    getSelectedStandardsAndSets(standardCollection);

  const getQuery = useCallback(
    (searchString) =>
      getSubjectsQuery(
        searchString,
        questionDifficulties,
        questionTypes,
        selectedStandardIds,
        selectedStandardSetIds,
        hideAssessmentQuestions,
        hidePreviouslyAssigned
      ),
    []
  );
  const fetchInitial = useMemo(() => classroomIds.length > 0, [classroomIds]);

  const {trackEvent} = useTracking({
    type: 'search bar',
    event: 'search',
    component: 'subject search'
  });

  return (
    <>
      <span
        className={classnames('u-display_flex u-bgc_transparent u-border-radius_1', {
          'subjects-filter__button-wrapper-active': selectedSubjects.size > 0
        })}
        ref={buttonRef}
      >
        <Button
          data-test-id='topics-modal__subject-dropdown'
          variant={(selectedSubjects.size ? undefined : 'outlined') as ButtonVariant}
          color={(selectedSubjects.size ? 'inherit' : 'secondary') as ButtonColor}
          onClick={() => setShowPopover(!showPopover)}
        >
          {`${selectedSubjects.size > 0 ? selectedSubjects.size : 'Choose'} ${pluralize(
            'Subject',
            selectedSubjects.size
          )}`}
          <Icon icon='caret-down' className='u-mar-l_2' />
        </Button>
        {selectedSubjects.size > 0 && (
          <IconButton color='inherit' icon='times' label='clear filter' onClick={onClear} />
        )}
      </span>
      {showPopover && (
        <Popover
          usePortal={false}
          border='none'
          expanded={showPopover}
          position='bottom-start'
          paddingLevel={null}
          targetRef={buttonRef}
          modifiers={{flip: {enabled: false}}}
        >
          <FindAndApply
            className='subjects-filter'
            query={getQuery}
            onApply={() => {}}
            onDismiss={() => setShowPopover(!showPopover)}
            fetchInitial={fetchInitial}
            defaultSelectedItems={selectedSubjects}
            onError={addGenericErrorToast}
          >
            <FindAndApply.Header paddingLevel={1}>
              {({HeaderSearchInput, searchString}) => (
                <HeaderSearchInput
                  data-test-id='topics-modal__subject-search'
                  variant
                  size='s'
                  placeholder='Search all subjects'
                  className='subjects-filter__search-input'
                  onBlur={() =>
                    searchString.length > 0 &&
                    trackEvent({
                      content: searchString
                    })
                  }
                />
              )}
            </FindAndApply.Header>
            <FindAndApply.Body
              className=''
              paddingLevel={0}
              beforeSearchMessage={
                fetchInitial
                  ? undefined
                  : ' Search for subjects like Grammar, Biology, or U.S. History'
              }
              noResultsMessage='No matches'
            >
              {({searchResults, selectedItems, handleChange, searchString}) => {
                return (
                  <SubjectsFilterResults
                    selectHandler={(subject) => {
                      handleChange(subject);
                      selectHandler(subject);
                    }}
                    selectedItems={selectedItems}
                    searchResults={searchResults}
                    searchString={searchString}
                    classroomIds={classroomIds}
                  />
                );
              }}
            </FindAndApply.Body>
            <FindAndApply.Footer paddingLevel={1}>
              {({ApplyButton, onDismiss}) => (
                <ButtonGroup align='right'>
                  <Button variant='text' color='secondary' size='s' onClick={onDismiss}>
                    Cancel
                  </Button>
                  <ApplyButton
                    variant='text'
                    size='s'
                    data-test-id='topics-modal__subjects-dropdown-done-btn'
                  >
                    Done
                  </ApplyButton>
                </ButtonGroup>
              )}
            </FindAndApply.Footer>
          </FindAndApply>
        </Popover>
      )}
    </>
  );
};

export default SubjectsFilter;
